import { ImageRotation } from 'shared/dtos/out/document';
import { canvasService } from '../canvasService';
import { RotatedImageSource } from './types';
import { blobLifecycleService } from 'services/fileOperations';

export class RotationService {
  async applyImageRotationToSource(
    source: string,
    imageRotation: number | undefined
  ): Promise<RotatedImageSource> {
    const rotation = imageRotation ?? ImageRotation.ROTATE_0;

    if (!rotation) {
      return { source };
    }

    const original = await canvasService.loadImageFromSource(source);
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (!context) {
      return { source };
    }

    const isQuarterTurn =
      rotation === ImageRotation.ROTATE_90 ||
      rotation === ImageRotation.ROTATE_270;

    canvas.width = isQuarterTurn ? original.height : original.width;
    canvas.height = isQuarterTurn ? original.width : original.height;

    context.translate(canvas.width / 2, canvas.height / 2);
    context.rotate((rotation * Math.PI) / ImageRotation.ROTATE_180);
    context.drawImage(original, -original.width / 2, -original.height / 2);

    const blob = await canvasService.canvasToBlob(canvas);
    if (!blob) {
      return { source };
    }

    const rotatedSource = blobLifecycleService.createTransientBlobUrl(blob);
    return {
      source: rotatedSource.url,
      cleanup: rotatedSource.cleanup,
    };
  }

  normalizeDrawingCanvasByImageRotation(
    drawingCanvas: HTMLCanvasElement,
    imageRotation: number | undefined
  ): HTMLCanvasElement {
    const rotation = imageRotation ?? ImageRotation.ROTATE_0;

    if (!rotation) {
      return drawingCanvas;
    }

    const normalizedCanvas = document.createElement('canvas');
    const isQuarterTurn =
      rotation === ImageRotation.ROTATE_90 ||
      rotation === ImageRotation.ROTATE_270;

    normalizedCanvas.width = isQuarterTurn
      ? drawingCanvas.height
      : drawingCanvas.width;
    normalizedCanvas.height = isQuarterTurn
      ? drawingCanvas.width
      : drawingCanvas.height;

    const context = normalizedCanvas.getContext('2d');
    if (!context) {
      return drawingCanvas;
    }

    context.translate(
      normalizedCanvas.width / 2,
      normalizedCanvas.height / 2
    );
    context.rotate((-rotation * Math.PI) / ImageRotation.ROTATE_180);
    context.drawImage(
      drawingCanvas,
      -drawingCanvas.width / 2,
      -drawingCanvas.height / 2
    );

    return normalizedCanvas;
  }
}

export const rotationService = new RotationService();

export type { RotatedImageSource } from './types';
